"use client";

import { useState } from "react";
import type { Route } from "next";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { Button, Card, Hairline, Input, Logo } from "@wmart/ui";
import { clientApi, ApiError } from "@/lib/api-client";

/**
 * Sign-in page (V1.0 customer self-serve).
 *
 * On success we redirect to the `?next=` param when it's a safe relative path
 * (so the price gate / cart / checkout return the buyer to where they were),
 * falling back to `/products`. The Better Auth POST to
 * `/api/auth/customer/sign-in` is preserved verbatim.
 *  - The "Forgot password?" link wires to the password-reset request page
 *    (added in P11-T9 follow-up; api endpoints landed in
 *    `password-reset.controller.ts`).
 *
 * Layout: single-column 480px Card, serif heading, primary submit button.
 */

/**
 * Resolve the post-sign-in redirect target. Only accept same-origin relative
 * paths (leading "/" but not "//", which would be a protocol-relative
 * off-site URL) — open-redirect guard. Falls back to /products.
 */
function safeNext(raw: string | null): string {
  if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
  return "/products";
}
export default function SignInPage() {
  const t = useTranslations("Auth");
  const tc = useTranslations("Common");
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await clientApi.post("/api/auth/customer/sign-in", { email, password });
      const next = safeNext(
        new URLSearchParams(window.location.search).get("next"),
      );
      // `next` is validated relative (safeNext); cast for typed-routes.
      router.push(next as Route);
      router.refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem?.detail ?? err.message);
      } else {
        setError(t("signInFailed"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-12">
      <Card className="p-8">
        <div className="mb-6 flex items-center justify-between">
          <a
            href="/"
            className="rounded-sm outline-none focus-visible:shadow-focus"
          >
            <Logo size="md" />
          </a>
          <p className="text-body-sm text-ink-500">
            {t("newToWmart")}{" "}
            <a
              href="/account/sign-up"
              className="rounded-sm text-accent underline underline-offset-2 outline-none focus-visible:shadow-focus"
            >
              {t("applyForAccount")}
            </a>
          </p>
        </div>
        <h1 className="font-serif text-display-sm text-ink-900">
          {t("signInTitle")}
        </h1>
        <p className="mt-2 text-body text-ink-700">{t("signInSubtitle")}</p>
        <Hairline spacing="md" />
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            name="email"
            type="email"
            label={t("email")}
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            name="password"
            type="password"
            label={t("password")}
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            {...(error ? { error } : {})}
          />
          <div className="-mt-1 flex justify-end">
            <a
              href="/account/password-reset/request"
              className="rounded-sm text-body-sm text-accent outline-none hover:underline focus-visible:shadow-focus"
            >
              {t("forgotPassword")}
            </a>
          </div>
          <Button type="submit" size="lg" full loading={busy}>
            {busy ? t("signingIn") : tc("signIn")}
          </Button>
        </form>
      </Card>
    </div>
  );
}
